
import { FilterOption } from '@/components/ui/FilterDropdown';
import { ImageType } from '@/components/image-generator/FilterBar/TypeFilter';

export interface FilterHandlersProps {
  setSelectedType: (type: ImageType | null) => void;
  setSelectedStyle: (value: string) => void;
  setSelectedInfluence: (value: string) => void;
  setSelectedBackground: (value: string) => void;
  styleOptions: FilterOption[];
  influenceOptions: FilterOption[];
  colorOptions: FilterOption[];
  backgroundOptions: FilterOption[];
  environmentOptions: FilterOption[];
  moodOptions: FilterOption[];
  setStyleOptions: (options: FilterOption[]) => void;
  setInfluenceOptions: (options: FilterOption[]) => void;
  setColorOptions: (options: FilterOption[]) => void;
  setBackgroundOptions: (options: FilterOption[]) => void;
  setEnvironmentOptions: (options: FilterOption[]) => void;
  setMoodOptions: (options: FilterOption[]) => void;
}

export const useFilterHandlers = ({
  setSelectedType,
  setSelectedStyle,
  setSelectedInfluence,
  setSelectedBackground,
  styleOptions,
  influenceOptions,
  colorOptions,
  backgroundOptions,
  environmentOptions,
  moodOptions,
  setStyleOptions,
  setInfluenceOptions,
  setColorOptions,
  setBackgroundOptions,
  setEnvironmentOptions,
  setMoodOptions,
}: FilterHandlersProps) => {
  // Handle type change with toggle functionality - now supports null for deselection
  const handleTypeChange = (type: ImageType | null) => {
    setSelectedType(type);
    
    // Only reset filters if a type is selected (not when deselecting)
    if (type !== null) {
      setSelectedStyle('');
      setSelectedInfluence('');
      setSelectedBackground('');
      
      // Reset all filter options
      resetFilterOptions(colorOptions, setColorOptions);
      resetFilterOptions(environmentOptions, setEnvironmentOptions);
      resetFilterOptions(moodOptions, setMoodOptions);
    }
  };

  // Helper to reset filter options
  const resetFilterOptions = (options: FilterOption[], setOptions: (options: FilterOption[]) => void) => {
    const resetOptions = options.map(option => ({
      ...option,
      checked: false
    }));
    setOptions(resetOptions);
  };

  // Handle updates to filter options without mutual exclusion logic
  const handleStyleChange = (options: FilterOption[]) => {
    setStyleOptions(options);
  };

  const handleInfluenceChange = (options: FilterOption[]) => {
    setInfluenceOptions(options);
  };

  const handleColorChange = (options: FilterOption[]) => {
    setColorOptions(options);
  };

  // Update handleBackgroundChange to use FilterOption[] consistently
  const handleBackgroundChange = (options: FilterOption[]) => {
    const checkedOption = options.find(opt => opt.checked);
    
    if (checkedOption) {
      setSelectedBackground(checkedOption.id);
    } else {
      setSelectedBackground('');
    }
    
    setBackgroundOptions(options);
  };
  
  const handleEnvironmentChange = (options: FilterOption[]) => {
    setEnvironmentOptions(options);
  };

  // Add handler for mood options without mutual exclusion
  const handleMoodChange = (updatedOptions: FilterOption[]) => {
    setMoodOptions(updatedOptions);
  };

  // Reset all filters
  const resetAllFilters = () => {
    setSelectedType(null);
    setSelectedStyle('');
    setSelectedInfluence('');
    setSelectedBackground('');
    setStyleOptions([]);
    setInfluenceOptions([]);
    setColorOptions([]);
    setBackgroundOptions([]);
    setEnvironmentOptions([]);
    setMoodOptions([]);
  };

  return {
    handleTypeChange,
    handleStyleChange,
    handleInfluenceChange,
    handleColorChange,
    handleBackgroundChange,
    handleEnvironmentChange,
    handleMoodChange,
    resetAllFilters,
  };
};
